import { useEffect, useRef } from 'react';

// Simple event bus implementation for cross-component communication
let eventBus = {
  subscribers: {},

  // Subscribe to an event with a callback
  on: function(event, callback) {
    if (!this.subscribers[event]) {
      this.subscribers[event] = [];
    }
    this.subscribers[event].push(callback);
    
    // Return a function to unsubscribe
    return () => {
      this.off(event, callback);
    };
  },

  // Unsubscribe from an event with a callback
  off: function(event, callback) {
    if (!this.subscribers[event]) return;
    
    // If callback is provided, remove only that callback
    if (callback) {
      this.subscribers[event] = this.subscribers[event].filter(cb => cb !== callback);
    } else {
      // If no callback provided, remove all subscribers for the event
      delete this.subscribers[event];
    }
  },

  // Emit an event with data to all subscribers
  emit: function(event, data) {
    if (!this.subscribers[event]) return;
    
    // Call all subscriber callbacks with the provided data
    this.subscribers[event].forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event bus subscriber for event "${event}":`, error);
      }
    });
  }
};

/**
 * A hook to use the global event bus throughout the application
 * Usage:
 * 
 * // To subscribe to events
 * const eventBus = useEventBus();
 * useEffect(() => {
 *   const unsubscribe = eventBus.on('my-event', (data) => {
 *     console.log('Event received:', data);
 *   });
 *   return unsubscribe;
 * }, [eventBus]);
 * 
 * // To emit events
 * const eventBus = useEventBus();
 * const handleClick = () => {
 *   eventBus.emit('my-event', { message: 'Button clicked' });
 * };
 */
export const useEventBus = () => {
  // Use a ref so we don't recreate the event bus on every render
  const busRef = useRef(eventBus);
  
  return busRef.current;
};

// Export a singleton instance that can be imported directly for use outside of React components
export const eventBusService = eventBus;

export default useEventBus;